import { API } from "aws-amplify";
import Auth from "@aws-amplify/auth";
import { InvokeCommand, InvokeWithResponseStreamCommand, LambdaClient } from "@aws-sdk/client-lambda";
import { Buffer } from "buffer";
import axios from "axios";
import { store } from "../store/index";

let process = {};

const SQL_SERVER_DB_TYPE = "SQL Server";
const SNOWFLAKE_DB_TYPE = "Snowflake";
const S3_WRITER = "SnowflakeS3Writer";
const DOCUMENT_SEARCH = "Document Search";

const api = "CRCRestAPI";

const requestQueue = new Map();

const getUser = () => {
  return store.getState().user;
};

console.log({ user: getUser() });
export const getRequest = async (api, path, myInit) => {
  await getEnvironmentVariables(api);
  const result = await API.get(api, path, myInit);
  return result;
};

export const postRequest = async (api, path, myInit) => {
  await getEnvironmentVariables(api);
  const result = await API.post(api, path, myInit);
  return result;
};

export const deleteRequest = async (api, path, myInit) => {
  await getEnvironmentVariables(api);
  const result = await API.del(api, path, myInit);
  return result;
};

const buildParams = async (dbType, payload) => {
  await getEnvironmentVariables(api);

  // JSON.stringify handles escaping (quotes, backslashes, newlines, etc.) correctly.
  // A prior hand-rolled version wrapped raw string values in quotes without escaping,
  // which produced invalid JSON whenever an agent-supplied string field (e.g. a
  // multi-line value) contained a newline or double quote.
  const filteredPayload = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
  );
  const payloadString = JSON.stringify(filteredPayload);

  return {
    // obviously do real
    FunctionName:
      dbType === S3_WRITER
        ? process.env.s3WriterFunctionName
        : process.env.snowFlakeFunctionName,
    InvocationType: "RequestResponse",
    LogType: "Tail",
    Payload: payloadString,
  };
};

export const GenerateAndDownloadFile = async (myInit) => {
  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      const params = await buildParams(myInit?.body?.dbType, {
        name: "s3Writer",
        query: myInit?.body?.writerSQL,
        fileName: myInit?.body?.fileName,
        key: credentials.identityId.split(":")[1],
        db: myInit?.body?.db,
        snowflakeServerFunctionName: process.env.snowFlakeFunctionName,
        checkedEntities: myInit?.body?.checkedEntities,
        sampleSize: myInit?.body?.sampleSize,
        limitToSpecified: myInit?.body?.limitToSpecified,
        email: myInit?.body?.email ?? getUser().email,
      });

      if (params !== undefined) {
        try {
          const command = new InvokeCommand(params);

          const lambda = new LambdaClient({
            region: "us-east-2",
            credentials: Auth.essentialCredentials(credentials),
          });

          const { Payload, FunctionError } = await lambda.send(command);
          const lambdaResult = Buffer.from(Payload).toString();

          // Timeouts and throws still come back as 200 - check FunctionError
          if (FunctionError) {
            console.error("s3Writer lambda failed:", FunctionError, lambdaResult);
            return reject(
              new Error(
                `Download generation failed (${FunctionError}): ${lambdaResult}`
              )
            );
          }

          let parsedResult;

          try {
            parsedResult = JSON.parse(lambdaResult);
          } catch (parseError) {
            return reject(
              new Error(`Unexpected response from download generation: ${lambdaResult}`)
            );
          }

          // Handler returns { signedUrl } on success, anything else means no zip
          if (!parsedResult?.signedUrl) {
            return reject(
              new Error(
                parsedResult?.errorMessage ??
                  "Download generation did not produce a file."
              )
            );
          }

          resolve({ success: true, data: parsedResult });
        } catch (error) {
          console.error(error);
          reject(error);
        }
      }
    });
  });
};

export const GetDistinctColumnValues = async (
  myInit,
  abortSignal,
  requestKey
) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getDistinctColumnValues",
    db: myInit?.body?.db,
    columnNames: myInit?.body?.columnNames,
    wheres: myInit?.body?.wheres ?? [],
    autoComplete: myInit?.body?.autoComplete ?? false,
    autoCompletePhrase: myInit?.body?.autoCompletePhrase ?? "",
    limit: myInit?.body?.limit ?? 0,
    page: myInit?.body?.page ?? 0,
    filterType: myInit?.body?.filterType ?? "",
    referenceTable: myInit?.body?.referenceTable ?? false,
    email: myInit?.body?.email ?? getUser().email,
  });

  //cancel previous request if exists
  if (requestQueue.has(requestKey)) {
    const previousRequest = requestQueue.get(requestKey);
    previousRequest.abort();
  }

  const abortController = new AbortController();
  requestQueue.set(requestKey, abortController);

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      if (params !== undefined) {
        try {
          const command = new InvokeCommand(params);
          const lambda = new LambdaClient({
            region: "us-east-2",
            credentials: Auth.essentialCredentials(credentials),
          });
          const { Payload } = await lambda.send(command, { abortSignal });
          const lambdaResult = Buffer.from(Payload).toString();
          resolve({ success: true, data: lambdaResult });
        } catch (error) {
          if (error.name !== "AbortError") {
            reject(error);
          }
        } finally {
          requestQueue.delete(requestKey);
        }
      }
    });
  });
};

//does not need fields either
export const GetQueryPatientCount = async (myInit) => {
  return await postRequest(api, "/api/v1/queryPatientCount", myInit);
};

//does not require fields because we return the whole patient data back
export const GetPatientCount = async (myInit, signal) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getPatientCount",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          },
            {
              signal: signal,
            }
          )
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetEncounterCount = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getEncounterCount",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetMedicationCount = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getMedicationCount",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetProcedureCount = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getProcedureCount",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetLabCount = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getLabCount",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetTopTenDiagnoses = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getTopTenDiagnoses",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetTopTenMedications = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getTopTenMedications",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetTopTenProcedures = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getTopTenProcedures",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetAgeDistribution = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getAgeDistribution",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetRaceDistribution = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getRaceDistribution",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetAdiScoreDistribution = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getAdiScoreDistribution",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetGenderDistribution = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getGenderDistribution",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetPatientDistributionByState = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getPatientDistributionByState",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetPatientHeatmapByState = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "getPatientHeatmapByState",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const QueryPatientData = async (myInit) => {
  const params = await buildParams(myInit?.body?.dbType, {
    name: "queryPatientData2",
    isNew: true,
    db: myInit?.body?.db,
    wheres: myInit?.body?.wheres ?? [],
    email: myInit?.body?.email ?? "",
    agentTable: myInit?.body?.agentTable,
  });

  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      try {
        axios
          .post(process.env.lambdaFunctionURL, {
            body: JSON.parse(params.Payload),
          })
          .then((value) => {
            resolve({ success: true, results: value.data });
          })
          .catch((exception) => {
            reject(exception);
          });
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  });
};

export const GetPatientData = async (myInit) => {
  //return await postRequest(api, "/api/v1/queryPatientSummary", myInit);
  try {
    const promiseArray = [];
    const paramsArray = [];
    const resultArray = [];

    const paramsGetPatientCount = await buildParams(myInit?.body?.dbType, {
      name: "getPatientCount",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetPatientCount);

    const paramsGetEncounterCount = await buildParams(myInit?.body?.dbType, {
      name: "getEncounterCount",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetEncounterCount);

    const paramsGetMedicationCount = await buildParams(myInit?.body?.dbType, {
      name: "getMedicationCount",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetMedicationCount);

    const paramsGetProcedureCount = await buildParams(myInit?.body?.dbType, {
      name: "getProcedureCount",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetProcedureCount);

    const paramsGetLabCount = await buildParams(myInit?.body?.dbType, {
      name: "getLabCount",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetLabCount);

    const paramsGetTopTenDiagnoses = await buildParams(myInit?.body?.dbType, {
      name: "getTopTenDiagnoses",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetTopTenDiagnoses);

    const paramsGetTopTenMedications = await buildParams(myInit?.body?.dbType, {
      name: "getTopTenMedications",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetTopTenMedications);

    const paramsGetTopTenProcedures = await buildParams(myInit?.body?.dbType, {
      name: "getTopTenProcedures",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetTopTenProcedures);

    const paramsGetAgeDistribution = await buildParams(myInit?.body?.dbType, {
      name: "getAgeDistribution",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetAgeDistribution);

    const paramsGetAdiScoreDistribution = await buildParams(myInit?.body?.dbType, {
      name: "getAdiScoreDistribution",
      isNew: true,
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsGetAdiScoreDistribution);

    const paramsGetGenderDistribution = await buildParams(
      myInit?.body?.dbType,
      {
        name: "getGenderDistribution",
        db: myInit?.body?.db,
        wheres: myInit?.body?.wheres ?? [],
        email: myInit?.body?.email ?? "",
      }
    );

    paramsArray.push(paramsGetGenderDistribution);

    const paramsGetPatientDistributionByState = await buildParams(
      myInit?.body?.dbType,
      {
        name: "getPatientDistributionByState",
        db: myInit?.body?.db,
        wheres: myInit?.body?.wheres ?? [],
        email: myInit?.body?.email ?? "",
      }
    );

    paramsArray.push(paramsGetPatientDistributionByState);

    const paramsGetPatientHeatmapByState = await buildParams(
      myInit?.body?.dbType,
      {
        name: "getPatientHeatmapByState",
        db: myInit?.body?.db,
        wheres: myInit?.body?.wheres ?? [],
        email: myInit?.body?.email ?? "",
      }
    );

    paramsArray.push(paramsGetPatientHeatmapByState);

    const paramsQueryPatientData = await buildParams(myInit?.body?.dbType, {
      name: "queryPatientData2",
      db: myInit?.body?.db,
      wheres: myInit?.body?.wheres ?? [],
      email: myInit?.body?.email ?? "",
    });

    paramsArray.push(paramsQueryPatientData);

    return await new Promise((resolve, reject) => {
      Auth.currentCredentials().then(async (credentials) => {
        try {
          /*
          const lambda = new LambdaClient({
            region: "us-east-2",
            credentials: Auth.essentialCredentials(credentials),
          });
  
          for (let params of paramsArray) {
            const command = new InvokeCommand(params);
            promiseArray.push(lambda.send(command));
          }
          
  
          Promise.all(promiseArray).then((values) => {
            for (let value of values) {
              const { Payload } = value;
              const result = Buffer.from(Payload).toString();
              const payload = JSON.parse(result);
              resultArray.push(payload);
            }
  
            resolve({ success: true, results: resultArray });
          });
          */
          for (let params of paramsArray) {
            promiseArray.push(
              axios.post(process.env.lambdaFunctionURL, {
                body: JSON.parse(params.Payload),
              })
            );
          }

          console.log({ promiseArray });
          Promise.all(promiseArray).then((values) => {
            for (let value of values) {
              resultArray.push(value.data);
            }

            resolve({ success: true, results: resultArray });
          });
        } catch (error) {
          console.error(error);
          reject(error);
        }
      });
    });
  } catch (exception) {
    console.error(exception);
  }
};

export const GetQueryRowCount = async (myInit) => {
  return await getRequest(api, "/api/v1/queryRowCount", myInit);
};

export const GetQueryResults = async (myInit) => {
  return await postRequest(api, "/api/v3/query", myInit);
};

export const GetAllFieldsByTable = async (tableName, dbType = "Snowflake") => {
  return await getRequest(api, "/api/v1/getAllFieldsByTable", {
    queryStringParameters: {
      dbType,
      tableName,
    },
  });
};

export const GetAllTables = async (dbType = "Snowflake") => {
  return await getRequest(api, "/api/v1/getAllFieldsByTable", {
    queryStringParameters: {
      dbType,
    },
  });
};

export const getSharedQueries = async (userID) => {
  if (userID === null || userID === undefined) {
    return [];
  }

  return await getRequest(api, "/api/v1/getSharedQueries", {
    queryStringParameters: {
      userID,
    },
  });
};

export const getUsersQueries = async (userID) => {
  if (userID === null || userID === undefined) {
    return [];
  }

  return await getRequest(api, "/api/v1/getUsersQueries", {
    queryStringParameters: {
      userID,
    },
  });
};

export const checkDownloads = async () => {
  return await getRequest(api, "/api/v1/checkDownloads", {});
};

export const saveQuery = async (myInit) => {
  return await postRequest(api, "/api/v1/saveQuery", myInit);
};

export const deleteMySQLQuery = async (queryID) => {
  return await deleteRequest(api, "/api/v1/deleteQuery", {
    queryStringParameters: {
      queryID,
    },
  });
};

export const deleteS3File = async (myInit) => {
  return await postRequest(api, "/api/v1/deleteFiles", myInit);
};

const getEnvironmentVariables = async (api) => {
  if (process.env === undefined) {
    process = await API.get(api, "/api/v1/getEnvironmentVariables", {});
  }
};

export const GetClinicalNotes = async (myInit) => {
  return await postRequest(api, "/api/v1/selectRandomClinicalNotes", myInit);
};

export const GetUserAssets = async (userID) => {

  if (userID === null || userID === undefined) {
    return [];
  }

  const maxAttempts = 3;
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await getRequest(api, "/api/v1/getUserAssets", {
        queryStringParameters: {
          userID,
        },
      });
    } catch (error) {
      lastError = error;
      // Aurora can be in a sleep state and needs a moment to wake up on the first hit
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
      }
    }
  }

  throw lastError;
};

export const getAccountLicenses = async () => {
  return await getRequest(api, "/api/v1/accountLicenses", {});
};

export const checkUserExists = async (email) => {
  return await getRequest(api, "/api/v1/checkUserExists", {
    queryStringParameters: { email },
  });
};

export const addUserLicenseForUser = async (email, accountLicenseID, expirationDate) => {
  return await postRequest(api, "/api/v1/addUserLicense", {
    body: { email, accountLicenseID, expirationDate },
  });
};

export const getUserAssetsByEmail = async (email) => {
  return await getRequest(api, "/api/v1/getUserAssetsByEmail", {
    queryStringParameters: { email },
  });
};

export const getAllUserAssets = async () => {
  return await getRequest(api, "/api/v1/getAllUserAssets", {});
};

export const getUsersByAdminGroup = async (group) => {
  return await getRequest(api, "/api/v1/getUsersByAdminGroup", {
    queryStringParameters: { group },
  });
};

export const removeUserLicense = async (fkUserID, accountLicenseID) => {
  return await postRequest(api, "/api/v1/removeUserLicense", {
    body: { fkUserID, accountLicenseID },
  });
};

export const updateAssetExpiration = async (fkUserID, accountLicenseID, expirationDate) => {
  return await postRequest(api, "/api/v1/updateUserAssetExpiration", {
    body: { fkUserID, accountLicenseID, expirationDate },
  });
};

/**
 * Create a server-side conversation thread via the Snowflake Cortex Threads API
 * (POST /api/v2/cortex/threads, proxied through the crcportalcortex Lambda).
 *
 * Threads persist conversation context in Snowflake, so each subsequent agent run
 * only needs to send the newest user message plus thread_id + parent_message_id.
 *
 * @returns {Promise<{ thread_id: number }>}
 */
export const CreateCortexThread = async () => {
  await getEnvironmentVariables(api);
  const credentials = await Auth.currentCredentials();
  const lambda = new LambdaClient({
    region: "us-east-2",
    credentials: Auth.essentialCredentials(credentials),
  });

  const command = new InvokeWithResponseStreamCommand({
    FunctionName: process.env.cortexFunctionName,
    Payload: JSON.stringify({ action: 'create_thread' }),
  });

  const { EventStream } = await lambda.send(command);
  const decoder = new TextDecoder();
  let buffer = '';
  for await (const event of EventStream) {
    if (event.PayloadChunk?.Payload) {
      buffer += decoder.decode(event.PayloadChunk.Payload, { stream: true });
    }
    if (event.InvokeComplete?.ErrorCode) {
      throw new Error(`Lambda error: ${event.InvokeComplete.ErrorDetails || event.InvokeComplete.ErrorCode}`);
    }
  }

  for (const line of buffer.split('\n')) {
    if (!line.trim()) continue;
    let parsed;
    try { parsed = JSON.parse(line); } catch { continue; }
    if (parsed.type === 'thread_created' && parsed.thread_id != null) return { thread_id: parsed.thread_id };
    if (parsed.type === 'error') throw new Error(parsed.message);
  }
  throw new Error('CreateCortexThread: Lambda response did not include a thread_id');
};

/**
 * Stream responses from the Snowflake Cortex Agent (PSYCH_AI_V2) via the crcportalcortex Lambda.
 *
 * Uses InvokeWithResponseStreamCommand so text chunks are delivered in real time as
 * the Lambda forwards them from Snowflake, rather than waiting for the full response.
 *
 * Threaded mode (recommended): create a thread with CreateCortexThread, then pass
 * threadId + parentMessageId (0 on the first turn, then the assistant_message_id
 * returned by the previous call) and only the newest user message in `messages` —
 * Snowflake holds the rest of the conversation server-side.
 * Stateless mode: pass threadId = 0 and the full conversation history in `messages`.
 *
 * @param {Array<{role: string, content: string}>} messages - New user message (threaded) or full history (stateless)
 * @param {number} threadId - Thread id from CreateCortexThread, or 0 for stateless
 * @param {number} parentMessageId - 0 for a thread's first message, else last assistant_message_id
 * @param {(chunk: string) => void} onChunk - Called with each answer token as it arrives
 * @param {(status: string) => void} [onStatus] - Called with tool-call progress labels
 * @param {(icon: string, text: string) => void} [onStep] - Called with structured progress steps (e.g. SQL)
 * @param {(token: string) => void} [onThinking] - Called with extended-thinking reasoning tokens
 * @param {(name: string, input: object) => void} [onToolCall] - Called with full tool name and input object
 * @param {object} [options] - { onRunId, onAgentStatus, abortSignal }
 * @param {(runId: string) => void} [options.onRunId] - Called once the run id is known; pass it to CancelCortexRun to stop the run
 * @param {(status: string, text: string) => void} [options.onAgentStatus] - Called with agent lifecycle status (planning, executing_tool, …)
 * @param {AbortSignal} [options.abortSignal] - Aborts the Lambda stream; resolves with aborted:true instead of throwing
 * @returns {{ thread_id: number, user_message_id: number|null, assistant_message_id: number|null, run_id: string|null, agent_table: string|null, cohort_error: string|null, aborted: boolean }}
 */
export const SendToCortexAgentStream = async (messages, threadId = 0, parentMessageId = 0, onChunk, onStatus, onStep, onThinking, onToolCall, options = {}) => {
  const { onRunId, onAgentStatus, abortSignal } = options;
  await getEnvironmentVariables(api);
  return await new Promise((resolve, reject) => {
    Auth.currentCredentials().then(async (credentials) => {
      const decoder = new TextDecoder();
      let lineBuffer = '';
      let returnedThreadId = threadId;
      let userMessageId = null;
      let assistantMessageId = null;
      let runId = null;
      let agentTable = null;
      let cohortError = null;

      // Whatever was received before an abort is still useful — the caller
      // keeps the partial answer and the activity timeline built from it.
      const partial = () => ({
        thread_id: returnedThreadId,
        user_message_id: userMessageId,
        assistant_message_id: assistantMessageId,
        run_id: runId,
        agent_table: agentTable,
        cohort_error: cohortError,
      });

      try {
        const lambda = new LambdaClient({
          region: "us-east-2",
          credentials: Auth.essentialCredentials(credentials),
        });

        const command = new InvokeWithResponseStreamCommand({
          FunctionName: process.env.cortexFunctionName,
          Payload: JSON.stringify({ messages, thread_id: threadId, parent_message_id: parentMessageId }),
        });

        const { EventStream } = await lambda.send(command, { abortSignal });

        for await (const event of EventStream) {
          if (event.PayloadChunk?.Payload) {
            lineBuffer += decoder.decode(event.PayloadChunk.Payload, { stream: true });

            // NDJSON: process all complete lines, keep any incomplete trailing line
            const lines = lineBuffer.split('\n');
            lineBuffer = lines.pop();

            for (const line of lines) {
              if (!line.trim()) continue;
              let parsed;
              try {
                parsed = JSON.parse(line);
              } catch (_) {
                console.warn('SendToCortexAgentStream: unparseable line:', line);
                continue;
              }
              if (parsed.type === 'chunk' && parsed.text) {
                onChunk(parsed.text);
              } else if (parsed.type === 'tool_call' && parsed.name) {
                if (onToolCall) onToolCall(parsed.name, parsed.input || {});
              } else if (parsed.type === 'agent_status' && parsed.text) {
                if (onAgentStatus) onAgentStatus(parsed.status || '', parsed.text);
              } else if (parsed.type === 'status' && parsed.text) {
                if (onStatus) onStatus(parsed.text);
              } else if (parsed.type === 'step' && parsed.text) {
                if (onStep) onStep(parsed.icon || '📋', parsed.text);
              } else if (parsed.type === 'thinking' && parsed.text) {
                if (onThinking) onThinking(parsed.text);
              } else if (parsed.type === 'run_started' && parsed.run_id) {
                runId = parsed.run_id;
                if (onRunId) onRunId(parsed.run_id);
              } else if (parsed.type === 'cohort_ready' && parsed.table) {
                agentTable = parsed.table;
              } else if (parsed.type === 'cohort_error' && parsed.message) {
                cohortError = parsed.message;
              } else if (parsed.type === 'done') {
                returnedThreadId = parsed.thread_id ?? threadId;
                userMessageId = parsed.user_message_id ?? null;
                assistantMessageId = parsed.assistant_message_id ?? null;
                runId = parsed.run_id ?? runId;
              } else if (parsed.type === 'error') {
                throw new Error(parsed.message);
              }
            }
          }

          if (event.InvokeComplete?.ErrorCode) {
            throw new Error(`Lambda error: ${event.InvokeComplete.ErrorDetails || event.InvokeComplete.ErrorCode}`);
          }
        }

        // Flush any remaining buffered content
        if (lineBuffer.trim()) {
          try {
            const parsed = JSON.parse(lineBuffer);
            if (parsed.type === 'done') {
              returnedThreadId   = parsed.thread_id ?? threadId;
              userMessageId      = parsed.user_message_id ?? null;
              assistantMessageId = parsed.assistant_message_id ?? null;
              runId              = parsed.run_id ?? runId;
            }
            if (parsed.type === 'chunk'    && parsed.text)           onChunk(parsed.text);
            if (parsed.type === 'status'   && parsed.text && onStatus)   onStatus(parsed.text);
            if (parsed.type === 'step'     && parsed.text && onStep)     onStep(parsed.icon || '📋', parsed.text);
            if (parsed.type === 'thinking' && parsed.text && onThinking) onThinking(parsed.text);
            if (parsed.type === 'agent_status' && parsed.text && onAgentStatus) onAgentStatus(parsed.status || '', parsed.text);
            if (parsed.type === 'run_started'  && parsed.run_id) { runId = parsed.run_id; if (onRunId) onRunId(parsed.run_id); }
            if (parsed.type === 'cohort_ready' && parsed.table)          agentTable = parsed.table;
            if (parsed.type === 'cohort_error' && parsed.message)        cohortError = parsed.message;
            if (parsed.type === 'error')                             throw new Error(parsed.message);
          } catch (_) { /* ignore incomplete trailing line */ }
        }

        resolve({ ...partial(), aborted: false });
      } catch (error) {
        // A user-initiated stop is a normal outcome, not a failure: resolve with
        // whatever streamed before the abort so the caller can keep the partial
        // answer instead of discarding the turn.
        if (abortSignal?.aborted || error?.name === 'AbortError') {
          resolve({ ...partial(), aborted: true });
          return;
        }
        console.error("SendToCortexAgentStream error:", error);
        reject(error);
      }
    }).catch(reject);
  });
};

/**
 * Cancel an in-flight Cortex Agent run (Snowflake Cancel Agent Run API,
 * POST /api/v2/cortex/agent/runs/{run_id}/cancel, proxied through the Lambda).
 *
 * Snowflake saves any partial output to the thread and bills for it. When it
 * reports an assistant_message_id for that partial turn, the caller should use
 * it as parent_message_id on the next turn to keep the thread linked correctly.
 *
 * @param {string} runId - Run id from SendToCortexAgentStream's onRunId callback
 * @returns {Promise<{ run_id: string, assistant_message_id: number|null }>}
 */
export const CancelCortexRun = async (runId) => {
  if (!runId) throw new Error('CancelCortexRun: runId is required');
  await getEnvironmentVariables(api);
  const credentials = await Auth.currentCredentials();
  const lambda = new LambdaClient({
    region: "us-east-2",
    credentials: Auth.essentialCredentials(credentials),
  });

  const command = new InvokeWithResponseStreamCommand({
    FunctionName: process.env.cortexFunctionName,
    Payload: JSON.stringify({ action: 'cancel_run', run_id: runId }),
  });

  const { EventStream } = await lambda.send(command);
  const decoder = new TextDecoder();
  let buffer = '';
  for await (const event of EventStream) {
    if (event.PayloadChunk?.Payload) {
      buffer += decoder.decode(event.PayloadChunk.Payload, { stream: true });
    }
    if (event.InvokeComplete?.ErrorCode) {
      throw new Error(`Lambda error: ${event.InvokeComplete.ErrorDetails || event.InvokeComplete.ErrorCode}`);
    }
  }

  for (const line of buffer.split('\n')) {
    if (!line.trim()) continue;
    let parsed;
    try { parsed = JSON.parse(line); } catch { continue; }
    if (parsed.type === 'run_cancelled') {
      return { run_id: parsed.run_id, assistant_message_id: parsed.assistant_message_id ?? null };
    }
    if (parsed.type === 'error') throw new Error(parsed.message);
  }
  throw new Error('CancelCortexRun: Lambda response did not confirm cancellation');
};

/**
 * Invoke the Snowflake Cortex Agent and return the full reply once complete.
 * Internally uses SendToCortexAgentStream and accumulates all chunks.
 *
 * @param {Array<{role: string, content: string}>} messages
 * @param {number} threadId
 * @returns {{ reply: string, thread_id: number }}
 */
export const SendToCortexAgent = async (messages, threadId = 0, parentMessageId = 0) => {
  let reply = '';
  const result = await SendToCortexAgentStream(messages, threadId, parentMessageId, (chunk) => {
    reply += chunk;
  });
  return { reply: reply.trim() || null, thread_id: result.thread_id, assistant_message_id: result.assistant_message_id };
};
